/**
 * Krok CRM1 — pomocne funkce nad sloupci pipeline poptavek v UI.
 *
 * ZADANI (důležité pro další údržbu): UI NEZNÁ stavový automat. Ten počítá
 * API a posílá ho na kartě jako `allowedTransitions` (stejně jako u zakázek
 * v kroku R4). Tady jsou jen tři věci, které automat neřeší:
 * seskupení řádků do sloupců, text „co hledá“ a počet dní ve fázi.
 */

export interface DemandStageView {
  readonly key: string;
  readonly label: string;
  readonly position: number;
  readonly terminal: boolean;
  readonly reopenable: boolean;
}

export interface DemandRow {
  readonly id: string;
  readonly title: string;
  readonly stage: string;
  readonly wantedLocality?: string | null | undefined;
  readonly wantedPriceFrom?: string | null | undefined;
  readonly wantedPriceTo?: string | null | undefined;
  readonly wantedCurrency?: string | undefined;
  readonly source?: string | undefined;
  readonly contactName?: string | null | undefined;
  readonly contactEmail?: string | null | undefined;
  readonly nextStep?: string | null | undefined;
  readonly stageEnteredAt?: string | undefined;
}

/** Vychozí pořadí sloupců — jen fallback, když API sloupce nevrátí. */
export const DEFAULT_STAGE_KEYS = [
  'new',
  'contacted',
  'viewing',
  'offer',
  'reservation',
  'contract',
  'lost',
] as const;

export interface StageGroup<T> {
  readonly stage: DemandStageView;
  readonly items: readonly T[];
}

/**
 * Rozdělí poptávky do sloupců. Poptávka ve fázi, kterou kancelář v konfiguraci
 * nemá, NESMÍ zmizet (krok O8 to opravoval u zakázek) — dostane vlastní
 * šedý sloupec na konci, odkud se dá přetáhnout do platného.
 */
export function groupByStage<T extends { readonly stage: string }>(
  rows: readonly T[],
  stages: readonly DemandStageView[],
): readonly StageGroup<T>[] {
  const ordered = [...stages].sort((a, b) => a.position - b.position || a.key.localeCompare(b.key));
  const buckets = new Map<string, T[]>(ordered.map((stage) => [stage.key, []]));
  const orphans = new Map<string, T[]>();
  for (const row of rows) {
    const bucket = buckets.get(row.stage);
    if (bucket !== undefined) {
      bucket.push(row);
    } else {
      const list = orphans.get(row.stage);
      if (list === undefined) orphans.set(row.stage, [row]);
      else list.push(row);
    }
  }
  const groups: StageGroup<T>[] = ordered.map((stage) => ({ stage, items: buckets.get(stage.key) ?? [] }));
  for (const [key, items] of orphans) {
    groups.push({
      stage: { key, label: key, position: 9000 + groups.length, terminal: false, reopenable: false },
      items,
    });
  }
  return groups;
}

function formatMoney(amount: string, currency: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  return new Intl.NumberFormat('cs-CZ', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Radek „co hledá“ pod nadpisem karty: lokalita + cenové rozmezí. Bez
 * jednoho z nich zbyde druhý; bez obou je řádek radši žádný než prázdný.
 */
export function demandSummary(row: DemandRow): string | null {
  const parts: string[] = [];
  if (typeof row.wantedLocality === 'string' && row.wantedLocality.trim() !== '') {
    parts.push(row.wantedLocality.trim());
  }
  const currency = row.wantedCurrency ?? 'CZK';
  const from = row.wantedPriceFrom;
  const to = row.wantedPriceTo;
  if (typeof to === 'string' && to !== '') parts.push(`do ${formatMoney(to, currency)}`);
  else if (typeof from === 'string' && from !== '') parts.push(`od ${formatMoney(from, currency)}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

/** Kolik dní karta strávila v aktuálním sloupci; nesmyslný vstup = 0, ne výjimka. */
export function daysInStage(stageEnteredAt: string | undefined, now: Date): number {
  if (typeof stageEnteredAt !== 'string' || stageEnteredAt === '') return 0;
  const entered = new Date(stageEnteredAt).getTime();
  if (Number.isNaN(entered)) return 0;
  const diff = Math.floor((now.getTime() - entered) / 86_400_000);
  return diff > 0 ? diff : 0;
}
